import { X, Phone, Mail, MapPin, Calendar, Loader2, Send } from "lucide-react";
import { STATUS_LABEL } from "./leadStatus";

function DetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center text-[#FF5A00] shrink-0">
        <Icon size={14} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-neutral-500">{label}</div>
        <div className="text-white">{value || "—"}</div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-6 pb-6 border-b border-white/5 last:border-b-0">
      <div className="text-xs uppercase tracking-[0.2em] text-[#FF5A00] mb-4">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between text-sm gap-4">
      <span className="text-neutral-500 shrink-0">{k}</span>
      <span className="text-white text-right">{v}</span>
    </div>
  );
}

function EmailStatusBadge({ sent }) {
  if (sent === false) {
    return (
      <span
        data-testid="admin-email-status-failed"
        className="text-xs px-3 py-1.5 rounded-full bg-red-500/15 border border-red-500/30 text-red-300"
      >
        ⚠ Email non envoyé à la création
      </span>
    );
  }
  if (sent === true) {
    return (
      <span
        data-testid="admin-email-status-sent"
        className="text-xs px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300"
      >
        ✓ Email envoyé
      </span>
    );
  }
  return null;
}

export function LeadDetailDrawer({ lead, onClose, onSetStatus, onResend, resending }) {
  if (!lead) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end md:items-stretch md:justify-end"
      onClick={onClose}
    >
      <div
        data-testid="admin-lead-detail"
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0a0a0a] border-l border-white/10 w-full md:max-w-xl overflow-y-auto max-h-[90vh] md:max-h-screen"
      >
        <div className="p-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <div className="eyebrow mb-2">Lead détaillé</div>
              <h2 className="font-display text-3xl">{lead.name}</h2>
            </div>
            <button
              onClick={onClose}
              className="text-neutral-500 hover:text-white"
              data-testid="admin-detail-close"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex gap-2 mb-6">
            {["new", "contacted", "won", "lost"].map((s) => (
              <button
                key={s}
                data-testid={`admin-detail-status-${s}`}
                onClick={() => onSetStatus(lead.id, s)}
                className={`pill ${lead.status === s ? "active" : ""}`}
              >
                {STATUS_LABEL[s]}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-6 pb-6 border-b border-white/5">
            <button
              type="button"
              onClick={() => onResend(lead.id)}
              disabled={resending}
              data-testid="admin-resend-email-btn"
              className="btn-primary text-sm px-4 py-2.5 disabled:opacity-50"
            >
              {resending ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Envoi…
                </>
              ) : (
                <>
                  <Send size={14} /> Me renvoyer par email
                </>
              )}
            </button>
            <EmailStatusBadge sent={lead.email_sent} />
          </div>

          <div className="space-y-3 mb-8 text-sm">
            <DetailRow icon={Phone} label="Téléphone" value={lead.phone} />
            <DetailRow icon={Mail} label="Email" value={lead.email} />
            <DetailRow
              icon={MapPin}
              label="Localisation"
              value={`${lead.address || ""} ${lead.city}`.trim()}
            />
            <DetailRow
              icon={Calendar}
              label="Demande reçue"
              value={new Date(lead.created_at).toLocaleString("fr-FR")}
            />
          </div>

          <Section title="Estimation">
            <KV k="Surface" v={lead.surface ? `${lead.surface} m²` : "—"} />
            <KV
              k="Budget"
              v={
                lead.estimated_min
                  ? `${lead.estimated_min.toLocaleString("fr-FR")} – ${lead.estimated_max.toLocaleString("fr-FR")} €`
                  : "—"
              }
            />
            <KV k="État support" v={lead.support_condition || "—"} />
            <KV k="Prestations" v={lead.services?.join(", ") || "—"} />
          </Section>

          <Section title="Configuration façade">
            <KV k="Couleur enduit" v={lead.enduit_color || "—"} />
            <KV k="Décoratif" v={lead.decorative_options?.join(", ") || "—"} />
            <KV k="Éclairage" v={lead.lighting_options?.join(", ") || "—"} />
            <KV k="Rénovation" v={lead.renovation_options?.join(", ") || "—"} />
            <KV k="Sur mesure" v={lead.custom_options?.join(", ") || "—"} />
          </Section>

          {lead.message && (
            <Section title="Message">
              <p className="text-sm text-neutral-300 whitespace-pre-line">
                {lead.message}
              </p>
            </Section>
          )}

          {(lead.facade_photo_base64 || lead.configured_photo_base64) && (
            <Section title="Photos">
              <div className="grid grid-cols-2 gap-3">
                {lead.facade_photo_base64 && (
                  <div>
                    <div className="text-xs text-neutral-500 mb-2">Façade originale</div>
                    <img
                      src={lead.facade_photo_base64}
                      alt="Façade"
                      className="w-full rounded-lg border border-white/10"
                      data-testid="admin-detail-photo-original"
                    />
                  </div>
                )}
                {lead.configured_photo_base64 && (
                  <div>
                    <div className="text-xs text-neutral-500 mb-2">Configuration</div>
                    <img
                      src={lead.configured_photo_base64}
                      alt="Config"
                      className="w-full rounded-lg border border-white/10"
                      data-testid="admin-detail-photo-config"
                    />
                  </div>
                )}
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}
